import React, { FC, useMemo, useState, useEffect } from "react";
import {
  Stack,
  TextInput,
  Textarea,
  Button,
  Group,
  Title,
  Box,
  Flex,
  Text,
  Tooltip,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { ProductAreaHeaderIcon } from "../../../../icons/x-symbol-svgrepo-com";
import { IconX, IconInfoCircle } from "@tabler/icons";
import BagelAccordionMenu from "../../../general/BagelAccordionMenu";
import {useKnowledgeData} from "../../../../hooks/useKnowledgeData";
import {useFields} from "../../../../hooks/useFields";

export interface AssignToNewIdeaFormProps {
  evidence?: any;
  onCancel: () => void;
  onSubmit: (values: any) => void;
  loading: boolean;
  title?: string;
  createThemeMode?: boolean;  
  createAsSubIdea?: boolean;
  createIdeaViaPopup?: boolean;
  /** Hide the sticky submit button row (e.g. when a parent modal provides its own footer). */
  hideActions?: boolean;
  /** Hide the title heading. Defaults to true (shown). */
  showTitle?: boolean;
  /** Called when title/description (or other form fields) change — for external submit controls. */
  onValuesChange?: (values: {
    title: string;
    description: string;
    domainId: string;
    componentId: string;
  }) => void;
}

const AssignToNewIdeaForm: FC<AssignToNewIdeaFormProps> = ({
  evidence,
  onCancel,
  onSubmit,
  loading,
  title,
  createThemeMode,
  createAsSubIdea,
  createIdeaViaPopup = false,
  hideActions = false,
  showTitle = true,
  onValuesChange,
}) => {
  // Form setup
  const form = useForm({
    initialValues: {
      title: evidence?.title || "",
      description: evidence?.text || "",
      domainId: evidence?.domainId || "",
      componentId: evidence?.componentId || "",
    },
    validate: {
      title: (v) => (!v?.trim() ? "Title is required" : null),
      description: (v) => (!v?.trim() ? "Description is required" : null),
      componentId: (v) =>
        createThemeMode ? null : !v?.trim() ? "Product area is required" : null,
    },
  });
  // Context
  const { allComponents, components } = useFields();
  const { domains } = useKnowledgeData();

  // Find the component (parent or child) with the evidence component id
  const parentComponent = components.find((c: any) => c._id === evidence?.componentId);
  const childComponent = components
    .flatMap((parent: any) => parent.childComponents || [])
    .find((child: any) => child._id === evidence?.componentId);
  const matchedComponent = parentComponent || childComponent;

  // Resolve selected component: prefer componentObj, then componentId in allComponents
  const component = useMemo(() => {
    if (evidence?.componentObj) {
      const byObj = allComponents.find((comp) => comp._id === evidence?.componentObj?._id);
      if (byObj) return byObj;
    }
    if (evidence?.componentId && allComponents.length) {
      return allComponents.find((comp) => comp._id === evidence.componentId) || null;
    }
    return null;
  }, [evidence?.componentObj, evidence?.componentId, allComponents]);

  const mlComponent = useMemo(() => {
    if (evidence?.ml?.mlComponentId) {
      return allComponents.find((comp) => comp._id === evidence.ml.mlComponentId);
    }
    return null;
  }, [evidence?.ml?.mlComponentId, allComponents]);

  // Is this an AI suggestion that hasn't been accepted yet?
  const isAiSuggestion = evidence?.ml?.mlComponentId && !evidence?.componentObj?._id;
  


  const [componentName, setComponentName] = useState<string | null>(matchedComponent?.name ?? component?.name ?? null);
  const [productArea, setProductArea] = useState<any>(component || (isAiSuggestion ? mlComponent : null));

  // Sync dropdown state when evidence/allComponents resolve (e.g. theme form with componentId)
  useEffect(() => {
    const resolved = component || (isAiSuggestion ? mlComponent : null) || matchedComponent;
    if (resolved) {
      setProductArea(resolved);
      setComponentName(resolved.name ?? null);
      if (resolved._id) {
        form.setFieldValue("componentId", resolved._id);
      }
      if (resolved.domain) {
        const domainObj = domains.find((d: any) => d.name === resolved.domain || d._id === resolved.domainId);
        if (domainObj) {
          form.setFieldValue("domainId", domainObj._id);
        }
      }
    }
  }, [component?._id, mlComponent?._id, matchedComponent?._id, isAiSuggestion]);

  useEffect(() => {
    onValuesChange?.({
      title: form.values.title,
      description: form.values.description,
      domainId: form.values.domainId,
      componentId: form.values.componentId,
    });
  }, [
    form.values.title,
    form.values.description,
    form.values.domainId,
    form.values.componentId,
    onValuesChange,
  ]);

  // Example flag that you might use
  const isGenerated = false;
  // Dummy displayState to mimic conditional rendering (adjust as needed)
  const displayState = { domain: true, component: true };

  // Handler to update the form when a domain is selected
  const handleDomainSelect = (selectedDomain: string) => {
    // Find the corresponding domain _id
    const domainObject = domains.find((d: any) => d.name === selectedDomain);
    if (domainObject) {
      // Update the form value for domainId
      form.setFieldValue("domainId", domainObject._id);
    }
    // Reset product area selection when domain changes
    setComponentName(null);
    setProductArea(null);
    form.setFieldValue("componentId", "");
  };

  const handleProductAreaSelect = async (item: any) => {
    setComponentName(item.name);
    setProductArea(item);
    form.setFieldValue("componentId", item._id || "");
    form.clearFieldError("componentId");
  };

  const handleSubmitWithCreateAsSubIdea = (asSubIdea: boolean) => {
    const result = form.validate();
    if (result.hasErrors || loading) return;
    onSubmit({ ...form.values, createAsSubIdea: asSubIdea });
  };

  return (
    <form
      data-testid="assign-to-new-idea-form"
      onSubmit={form.onSubmit((values) => {
        if (!loading) onSubmit({ ...values, createAsSubIdea: createAsSubIdea ?? false });
      })}
    >
      <Group position="apart">
        { showTitle && <Title order={5} mb="xs">
          {title || "Assign to a new idea"}
        </Title> }

        {!createThemeMode && !createIdeaViaPopup && (
          <Button size="xs" variant="outline" radius="md" color="gray" onClick={onCancel} leftIcon={<IconX size={16} />}>
              Cancel
          </Button>
        )}
      </Group>
      <Stack spacing={10}>
        <TextInput
          label="Title"
          radius="md"
          placeholder="Idea title"
          withAsterisk
          {...form.getInputProps("title")}
        />
        <Textarea
          label="Description"
          radius="md"
          placeholder="Idea description"
          withAsterisk
          minRows={2}
          maxRows={12}
          autosize
          styles={{ input: { overflowY: 'auto' } }}
          {...form.getInputProps("description")}
        />
       
        <Group>
          {/* Product Area Component */}
          {displayState?.component && !createThemeMode && (
            <Box>
              <Flex ml="4px" align="center" mb="4px">
                <ProductAreaHeaderIcon />
                <Text fz="12px" color="#585C68" ml="6px" sx={{ lineHeight: "16px" }}>
                  Product Area
                  <Text span c="red" ml={2} aria-hidden>
                    *
                  </Text>
                </Text>
              </Flex>
              <BagelAccordionMenu
                selectedComponent={productArea}

                isAiSuggestion={false}
                onSelect={(item: any, type: string) => {
                  if (type === 'Domain') {
                    // Handle domain selection if needed
                    // This is handled by the useMemo already
                  } else {
                    // Handle product area selection
                    
                    handleProductAreaSelect(item);
                  }
                }}
              >
                <Flex
                  h={24}
                  px={8}
                  py={4}
                  mx="2px"
                  bg="#5C5CEB1A"
                  justify="space-evenly"
                  align="center"
                  sx={{
                    borderRadius: "4px",
                    border: "0.8px solid #ced4da",
                    cursor: isGenerated ? "default" : "pointer",
                  }}
                >
                  <Text
                    fz="12px"
                    lineClamp={1}
                    tt="capitalize"
                    miw="85px"
                  >
                    {componentName && componentName !== "Unassigned" ? 
                      <Text span color="dimmed">.../ </Text> 
                      : null}
                    {productArea?.name || "Unassigned"}
                  </Text>
                </Flex>
              </BagelAccordionMenu>
              {form.errors.componentId && (
                <Text size="xs" color="red" mt={6} ml="4px">
                  {form.errors.componentId}
                </Text>
              )}
            </Box>
          )}
        </Group>

        {/* Form action buttons */}
        {!hideActions && (
          <Group
            sx={{
              position: "sticky",
              bottom: 0,
              background: "#F5F6FE",
              paddingBottom: "1rem",
            }}
            mt="md"
            noWrap
          >
            {createThemeMode && createAsSubIdea ? (
              <>
                <Button
                  fullWidth
                  type="button"
                  radius="md"
                  loading={loading}
                  size="12px"
                  onClick={() => handleSubmitWithCreateAsSubIdea(true)}
                  styles={(theme) => ({
                    root: {
                      backgroundColor: "#5C5CEB",
                      border: 0,
                      height: 35
                    },
                    leftIcon: { marginRight: 15 },
                  })}
                  sx={{
                    border: "1px solid #5C5CEB",
                    color: "#FFFFFF",
                  }}
                >
                  Create as Sub-idea
                </Button>
                <Button
                  fullWidth
                  type="button"
                  size="12px"
                  radius="md"
                  loading={loading}
                  onClick={() => handleSubmitWithCreateAsSubIdea(false)}
                  styles={(theme) => ({
                    root: {
                      backgroundColor: "#FFFFFF",
                      border: 0,
                      height: 35,
                      "&:hover": {
                        backgroundColor: theme.fn.darken("#EAEAFE", 0.05),
                      },
                    },
                    leftIcon: { marginRight: 15 },
                  })}
                  sx={{
                    border: "1px solid #5C5CEB",
                    color: "#5C5CEB",
                  }}
                >
                  Create as new Idea
                </Button>
              </>
            ) : (
              <Button
                data-testid="assign-to-new-idea-submit"
                fullWidth
                type="button"
                radius="md"
                loading={loading}
                onClick={() => handleSubmitWithCreateAsSubIdea(false)}
                styles={(theme) => ({
                  root: {
                    backgroundColor: "#FFFFFF",
                    border: 0,
                    height: 35,
                    "&:hover": {
                      backgroundColor: theme.fn.darken("#EAEAFE", 0.05),
                    },
                  },
                  leftIcon: { marginRight: 15 },
                })}
                sx={{
                  border: "1px solid #5C5CEB",
                  color: "#5C5CEB",
                }}
              >
                <Group spacing={6} noWrap position="center">
                  <span>{title || "Create Idea with This Evidence"}</span>
                  { createThemeMode && !createAsSubIdea && (
                    <Tooltip label="This theme belongs to a sub-idea. sub-ideas can't be nested" withArrow>
                      <Box component="span" sx={{ display: "inline-flex", cursor: "pointer" }}>
                        <IconInfoCircle size={16} />
                      </Box>
                    </Tooltip>
                  )}
                </Group>
              </Button>
            )}
          </Group>
        )}
      </Stack>
    </form>
  );
};

export default AssignToNewIdeaForm;